import { FC, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PUSH_TARGET_PATH_PATTERN } from "@myapp/contract";

/**
 * Routes a notification tap that landed on an already-open window.
 *
 * The service worker deliberately does not call `client.navigate()` for this: navigating would
 * throw away the running SPA and become a fourth reload path next to `useAppUpdate`,
 * `reloadForMaintenance` and the axios refresh redirect. It focuses the window and posts
 * `PUSH_NAVIGATE` instead; this listener turns that into a normal client-side route change.
 *
 * Lives in `app/` because it needs the router. Headless.
 */
export const PushNavigator: FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | null;
      if (!data || data.type !== "PUSH_NAVIGATE") return;
      // Same pattern the service worker itself checks before posting, and the one the admin
      // compose form validates against: one leading slash, no second `/` or `\`. A bare
      // `startsWith("/")` would let `//evil.com` or `/\evil.com` — both protocol-relative —
      // send the user off-site.
      if (typeof data.url !== "string" || !PUSH_TARGET_PATH_PATTERN.test(data.url)) return;
      navigate(data.url);
    };

    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);

  return null;
};
